<template>
    <pendo-popover
        :open="open"
        popover-class="label-popover"
        @apply-hide="$emit('closed')">
        <template #popover>
            <pendo-form
                ref="labelForm"
                v-model="isValid"
                :model="editingLabel">
                <pendo-form-item
                    prop="name"
                    :rules="rules.name"
                    label="Label Name">
                    <pendo-input
                        ref="labelName"
                        v-model="editingLabel.name"
                        placeholder="Unnamed">
                        <template #suffix>
                            <div
                                class="character-count"
                                :class="{ 'over-limit': editingLabel.name.length > 25 }">
                                {{ editingLabel.name.length }} / 25
                            </div>
                        </template>
                    </pendo-input>
                </pendo-form-item>
                <pendo-form-item>
                    <pendo-checkbox
                        v-model="editingLabel.isPrivate"
                        label="Set Private"
                        :disabled="!canAddPublic" />
                </pendo-form-item>
            </pendo-form>
            <div class="footer-actions">
                <pendo-button
                    size="mini"
                    type="secondary"
                    theme="app"
                    label="Cancel"
                    @click="close" />
                <pendo-button
                    size="mini"
                    theme="app"
                    label="Save"
                    :disabled="!isValid"
                    :loading="saving"
                    @click="validateBeforeSave" />
            </div>
        </template>
    </pendo-popover>
</template>

<script>
import {
    PendoButton,
    PendoForm,
    PendoFormItem,
    PendoInput,
    PendoNotification,
    PendoPopover,
    PendoCheckbox,
} from '@pendo/components';
import { mapActions } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

export default {
    name: 'LabelPopover',
    components: {
        PendoButton,
        PendoForm,
        PendoFormItem,
        PendoInput,
        PendoPopover,
        PendoCheckbox,
    },
    props: {
        availableLabels: {
            type: Array,
            required: true,
        },
        canAddPublic: {
            type: Boolean,
            default: false,
        },
        entityId: {
            type: String,
            default: null,
        },
        entityType: {
            type: String,
            required: true,
        },
        labelToEdit: {
            type: Object,
            default: () => ({
                name: '',
                isPrivate: true,
            }),
        },
        prefill: {
            type: String,
            default: '',
        },
    },
    emits: ['label-added', 'label-edited', 'closed'],
    data () {
        return {
            open: true,
            isValid: false,
            editingLabel: cloneDeep(this.labelToEdit),
            rules: {
                name: [
                    {
                        required: true,
                        message: 'Label name is required',
                        trigger: ['keyup', 'blur'],
                    },
                    {
                        max: 25,
                        message: 'Maximum 25 characters',
                        trigger: ['keyup', 'blur'],
                    },
                    {
                        validator: this.validateUniqueOrOnEntity,
                        trigger: ['keyup', 'blur'],
                    },
                ],
            },
            saving: false,
        };
    },
    computed: {
        isEditing () {
            return !isEqual(this.labelToEdit, { name: '', isPrivate: true });
        },
    },
    created () {
        if (!this.labelToEdit.name) {
            this.editingLabel.name = this.prefill;
        }
    },
    async mounted () {
        // autofocus name input after popover mounts
        setTimeout(() => {
            this.$refs.labelName.$refs.input.focus();
            if (this.prefill) this.$refs.labelForm.validateForm();
        }, 50);
    },
    methods: {
        ...mapActions('labels', ['create', 'createOnEntity', 'updateLabelsOnEntity', 'editLabel']),
        close () {
            this.open = false;
        },
        findMatchingLabel () {
            const { id, isPrivate, name } = this.editingLabel;
            const nameLower = name.toLowerCase();

            return this.availableLabels.find(
                (label) => label.name.toLowerCase() === nameLower && !!label.isPrivate === isPrivate && label.id !== id,
            );
        },
        validateBeforeSave () {
            this.$refs.labelForm.validate(async (valid) => {
                if (valid) {
                    await this.save();
                }
            });
        },
        async save () {
            let errorVerb;

            try {
                this.saving = true;

                if (this.isEditing) {
                    errorVerb = 'updat';
                    await this.patchLabel();
                } else {
                    const matchingLabel = this.findMatchingLabel();
                    const isOnEntity = !!this.entityId;
                    errorVerb = 'creat';

                    if (isOnEntity) {
                        if (matchingLabel) {
                            errorVerb = 'add';
                            await this.addLabelToEntity(matchingLabel);
                        } else {
                            await this.createLabelOnEntity();
                        }
                    } else if (matchingLabel) {
                        // form validation should prevent this
                        PendoNotification({
                            type: 'error',
                            title: 'Label already exists',
                            message: 'The label you tried to create already exists.',
                            duration: 3000,
                        });
                    } else {
                        await this.createLabel();
                    }
                }

                this.saving = false;
                this.close();
            } catch {
                this.saving = false;
                PendoNotification({
                    type: 'error',
                    title: `Label was not ${errorVerb}ed`,
                    message: `Looks like something went wrong when ${errorVerb}ing your label. Please refresh and try again.`,
                    duration: 3000,
                });
            }
        },
        async createLabel () {
            const newLabel = await this.create(this.editingLabel);
            this.$emit('label-added', newLabel);
            PendoNotification({
                type: 'success',
                title: 'Label created',
                message: 'The label was created successfully.',
                duration: 3000,
            });
        },
        async createLabelOnEntity () {
            const newLabel = await this.createOnEntity({
                ...this.editingLabel,
                memberId: this.entityId,
                memberType: this.entityType,
            });
            this.$emit('label-added', newLabel);
            PendoNotification({
                type: 'success',
                title: 'Label created',
                message: 'The label was created and applied successfully.',
                duration: 3000,
            });
        },
        async addLabelToEntity (matchingLabel) {
            await this.updateLabelsOnEntity({
                addLabelIds: [matchingLabel.id],
                removeLabelIds: [],
                memberId: this.entityId,
                memberType: this.entityType,
            });
            this.$emit('label-added', matchingLabel);
            PendoNotification({
                type: 'success',
                title: 'Label applied',
                message: 'The label you tried to create already exists, so the existing one was applied.',
                duration: 3000,
            });
        },
        async patchLabel () {
            await this.editLabel({
                labelId: this.editingLabel.id,
                labelName: this.editingLabel.name,
                isPrivate: !!this.editingLabel.isPrivate,
            });
            this.$emit('label-edited', this.editingLabel);
            PendoNotification({
                type: 'success',
                title: 'Label updated',
                message: 'The label was updated successfully.',
                duration: 3000,
            });
        },
        validateUniqueOrOnEntity (rule, val, cb) {
            if ((!this.entityId || this.isEditing) && this.findMatchingLabel()) {
                cb(new Error('This label already exists'));
            } else {
                cb();
            }
        },
    },
};
</script>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.label-popover {
    width: 252px;

    .pendo-popover__inner {
        padding: 16px;
    }

    .character-count {
        white-space: nowrap;

        &.over-limit {
            color: var(--palette-red-60);
        }
    }

    .footer-actions {
        display: flex;
        justify-content: space-between;
    }
}
</style>
